
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../lib/firebase';
import firebase from 'firebase/compat/app';
import { createUserProfile } from '../services/authService';
import { UserProfile, UserRole } from '../types';

interface AuthContextType {
  user: firebase.User | null;
  profile: UserProfile | null;
  loading: boolean;
  role: UserRole | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  role: null,
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<firebase.User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let profileUnsub: (() => void) | null = null;

    const authUnsub = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      
      // Clean up previous profile listener if user changes
      if (profileUnsub) {
          profileUnsub();
          profileUnsub = null;
      }

      if (currentUser) {
        // Real-time listener for profile changes (fixes Calendar Sync UI latency)
        profileUnsub = db.collection('users').doc(currentUser.uid).onSnapshot(async (doc) => {
            if (doc.exists) {
                const userData = doc.data() as UserProfile;
                setProfile(userData);
            } else {
                // Profile doesn't exist, create it (Self-healing)
                await createUserProfile(currentUser, 'CLIENT');
                // The creation will trigger this snapshot listener again with the new data
            }
            setLoading(false);
        }, (err) => {
            console.error("Profile subscription error", err);
            setLoading(false);
        });
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
        authUnsub();
        if (profileUnsub) profileUnsub();
    };
  }, []);

  const logout = async () => {
    await auth.signOut();
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, role: profile?.role || null, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
